// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Conduit — one player's run: the plate, the four fittings in hand, and the scorer.
//
// Deliberately pure. No DOM, no network, no clock. The bots, the balance sim and the browser all
// drive this same object, which is the only reason a sim result means anything about the shipped
// game — a sim playing a parallel implementation measures the parallel implementation.
//
// FORCED USE is the rule that makes the ORDER of the four fittings the actual skill: every fitting
// must be committed somewhere legal, so you cannot bank the awkward one. The escape hatch is narrow
// and PROVEN rather than assumed — SCRAP unlocks only when an exhaustive sweep over all 49 cells and
// every rotation finds genuinely nothing, and the audit re-runs that sweep itself at that exact
// prior state rather than believing the flag.

import { CULVERT, FILAMENT, FORMS, type Dir, type PieceId } from './pieces';
import {
  VENT_COUNT,
  canPlace,
  clearCell,
  clonePlate,
  emptyPlate,
  legalSpots,
  refuse,
  setCell,
  weepsOf,
  type Plate,
  type Refusal,
  type Spot,
} from './plate';
import {
  buildGraph,
  largestVentComponent,
  longestRun,
  noWeeps,
  offPathCells,
  openEnds,
  sealedVents,
  ventComponentSizes,
  weepKey,
  type WeepSet,
} from './score';
import { PER_ROUND, type Mode } from './modes';
import { dealRun, type Run } from './deal';

export interface Move {
  readonly slot: number;
  readonly piece: PieceId;
  readonly rot: number;
  readonly x: number;
  readonly y: number;
}

export interface LogEntry extends Move {
  readonly round: number;
  /** Half-edges this placement paid for (Seep). Empty everywhere else. */
  readonly weeps: number[];
  /** True when nothing fit and the fitting was thrown away instead. */
  readonly scrapped: boolean;
  /** True when the multiplayer clock placed it for you. */
  readonly assisted: boolean;
}

export interface Terms {
  claim: number;
  net: number;
  run: number;
  ends: number;
  offPath: number;
  weep: number;
  scrap: number;
  total: number;
}

export interface ScoreDetail extends Terms {
  /** Per-vent: -1 not sealed, else the round in which it sealed. Drives the claim tier off-plate. */
  sealedIn: number[];
  largest: number;
  runC: number;
  runF: number;
  pathC: number[];
  pathF: number[];
  bounded: boolean;
  maxExpansions: number;
}

/**
 * One player's run.
 *
 * `sealedIn` is recorded as the run goes rather than derived at the end, because a vent's CLAIM tier
 * depends on WHICH ROUND you sealed it, and that is not recoverable from the finished plate. It is
 * also the only part of the score that depends on the rest of the table, which is why it travels as
 * its own 12-byte ledger and is resolved locally on every peer.
 */
export class Game {
  readonly run: Run;
  readonly mode: Mode;
  readonly plate: Plate = emptyPlate();
  readonly log: LogEntry[] = [];
  /** Round index in the run, 0-based. */
  round = 0;
  /**
   * Slots of this round's four fittings that have NOT been spent yet, in dealt order.
   *
   * The player picks WHICH of these to spend, not merely where to put the next one — that choice is
   * the entire skill of the game, because every mark rewrites what is legal for the rest of the
   * round. An earlier draft spent them in dealt order and the strong bot scored identically to the
   * greedy one on every seed in every mode, which is exactly what "there is no decision here" looks
   * like in a number.
   */
  remaining: number[] = [0, 1, 2, 3];
  over = false;
  /** Round in which each vent first sealed, -1 for never. */
  readonly sealedIn: number[] = new Array(VENT_COUNT).fill(-1);
  private weeps: WeepSet = noWeeps();

  constructor(runOrMode: Run | Mode, seed?: number) {
    if ('rounds' in runOrMode && Array.isArray((runOrMode as Run).rounds)) {
      this.run = runOrMode as Run;
    } else {
      this.run = dealRun(runOrMode as Mode, seed ?? 1);
    }
    this.mode = this.run.mode;
  }

  /** The four fittings this round, in slot order. */
  get hand(): readonly PieceId[] {
    return this.run.rounds[Math.min(this.round, this.run.rounds.length - 1)].fittings;
  }

  /**
   * The slots the player may choose between right now.
   *
   * Full lookahead means every unspent fitting; Seep reveals one at a time, so it is whichever came
   * next — which is precisely why Seep's ordering skill disappears and its mode axis is commitment.
   */
  available(): number[] {
    if (this.over) return [];
    return this.mode.reveal >= PER_ROUND ? [...this.remaining] : this.remaining.slice(0, this.mode.reveal);
  }

  /** The fitting in a given slot. */
  pieceAt(slot: number): PieceId | null {
    return this.hand[slot] ?? null;
  }

  /** How many of this round's fittings the player can currently see. */
  visibleCount(): number {
    return this.available().length;
  }

  get marksPlaced(): number {
    return this.log.filter((e) => !e.scrapped).length;
  }

  private get relaxOpts(): { relaxed?: boolean } {
    return this.mode.relaxed ? { relaxed: true } : {};
  }

  /** Every legal (cell, rotation) for the fitting in a given slot. */
  spots(slot: number): Spot[] {
    const p = this.pieceAt(slot);
    return p && this.remaining.includes(slot) ? legalSpots(this.plate, p, this.relaxOpts) : [];
  }

  /** Why a specific placement would be refused, or null. Feeds the SET button's teaching text. */
  refusalAt(slot: number, rot: number, x: number, y: number): Refusal | null {
    const p = this.pieceAt(slot);
    if (!p) return { kind: 'taken' };
    const f = FORMS[p][rot];
    if (!f) return { kind: 'taken' };
    return refuse(this.plate, f, x, y, this.relaxOpts);
  }

  /**
   * Whether a fitting has nowhere at all to go — PROVEN by an exhaustive sweep, never assumed.
   *
   * In Seep this is almost never true (nearly everything is legal at a price), which is exactly
   * right: the mode's whole point is that you always CAN place, badly.
   */
  mustScrap(slot: number): boolean {
    return this.pieceAt(slot) !== null && this.spots(slot).length === 0;
  }

  /** True when NONE of the available fittings can be placed — the only time SCRAP unlocks. */
  stuck(): boolean {
    const av = this.available();
    return av.length > 0 && av.every((s) => this.mustScrap(s));
  }

  /** Rotations that are legal at a given cell, in ascending order. Drives the rotate button. */
  legalRotsAt(slot: number, x: number, y: number): number[] {
    const p = this.pieceAt(slot);
    if (!p) return [];
    const out: number[] = [];
    for (let r = 0; r < FORMS[p].length; r++) {
      if (canPlace(this.plate, FORMS[p][r], x, y, this.relaxOpts)) out.push(r);
    }
    return out;
  }

  /** What a placement would cost in weeps, for Seep's cost tint. */
  weepCountAt(slot: number, rot: number, x: number, y: number): number {
    const p = this.pieceAt(slot);
    if (!p || !this.mode.relaxed) return 0;
    const f = FORMS[p][rot];
    if (!f) return 0;
    return weepsOf(this.plate, f, x, y).length;
  }

  place(slot: number, rot: number, x: number, y: number, assisted = false): boolean {
    const piece = this.pieceAt(slot);
    if (!piece || this.over || !this.available().includes(slot)) return false;
    const f = FORMS[piece][rot];
    if (!f || !canPlace(this.plate, f, x, y, this.relaxOpts)) return false;

    const w = this.mode.relaxed ? weepsOf(this.plate, f, x, y) : [];
    setCell(this.plate, piece, rot, x, y);
    for (const d of w) this.weeps[weepKey(x, y, d)] = 1;

    this.log.push({ round: this.round, slot, piece, rot, x, y, weeps: w.slice(), scrapped: false, assisted });
    this.afterMark(slot);
    return true;
  }

  /** Throw a fitting away. Only legal when the sweep proves it fits nowhere. */
  scrap(slot: number): boolean {
    const piece = this.pieceAt(slot);
    if (!piece || this.over || !this.available().includes(slot) || !this.mustScrap(slot)) return false;
    this.log.push({ round: this.round, slot, piece, rot: 0, x: -1, y: -1, weeps: [], scrapped: true, assisted: false });
    this.afterMark(slot);
    return true;
  }

  /**
   * Undo the last mark of the CURRENT round.
   *
   * Bounded to the round on purpose: at the round barrier your ledger has already been broadcast and
   * is on somebody else's summary, and unwinding a number a rival has already seen is worse than not
   * offering undo at all.
   */
  undo(): boolean {
    const last = this.log[this.log.length - 1];
    if (!last || last.round !== this.round || this.over) return false;
    this.log.pop();
    if (!last.scrapped) {
      clearCell(this.plate, last.x, last.y);
      for (const d of last.weeps) this.weeps[weepKey(last.x, last.y, d as Dir)] = 0;
    }
    // Put the slot back where it was in dealt order, so the tray never reshuffles under the player.
    this.remaining.push(last.slot);
    this.remaining.sort((a, b) => a - b);
    this.recomputeSeals();
    return true;
  }

  get canUndo(): boolean {
    const last = this.log[this.log.length - 1];
    return !!last && last.round === this.round && !this.over;
  }

  private afterMark(slot: number): void {
    this.remaining = this.remaining.filter((s) => s !== slot);
    this.recomputeSeals();
    if (this.remaining.length === 0) {
      this.round++;
      if (this.round >= this.mode.rounds) {
        this.round = this.mode.rounds - 1;
        this.over = true;
      }
      this.remaining = [0, 1, 2, 3];
    }
  }

  /**
   * Record the round in which each vent first sealed.
   *
   * Recomputed from scratch after every mark rather than patched, so an undo cannot leave a stale
   * seal behind. Cheap: union-find over at most 98 nodes.
   */
  private recomputeSeals(): void {
    const g = buildGraph(this.plate, this.weeps);
    const sealed = sealedVents(g);
    for (let v = 0; v < VENT_COUNT; v++) {
      if (sealed[v] && this.sealedIn[v] < 0) this.sealedIn[v] = this.round;
      else if (!sealed[v]) this.sealedIn[v] = -1;
    }
  }

  /** True the instant a round's four marks are all spent — the barrier where the ledger goes out. */
  get roundComplete(): boolean {
    return this.remaining.length === PER_ROUND && this.log.length > 0;
  }

  /** How many of this round's four fittings are already spent. Drives the HUD. */
  get spentThisRound(): number {
    return PER_ROUND - this.remaining.length;
  }

  /**
   * Score the plate.
   *
   * `claimTier` maps a vent to 0 (among the earliest at the table) or 1 (later). Solo and any peer
   * with no ledgers yet passes all-zeros, which is the optimistic reading and is corrected the
   * instant a rival's ledger lands.
   */
  score(claimTier?: (vent: number) => 0 | 1): ScoreDetail {
    return scorePlate(this.plate, this.mode, {
      sealedIn: this.sealedIn,
      weeps: this.weeps,
      scraps: this.log.filter((e) => e.scrapped).length,
      claimTier,
    });
  }

  get weepSet(): WeepSet {
    return this.weeps;
  }

  /** Restore a plate wholesale — used by the storage replay after a mid-match refresh. */
  restore(from: Game): void {
    this.plate.piece.set(from.plate.piece);
    this.plate.rot.set(from.plate.rot);
    this.weeps = Uint8Array.from(from.weepSet);
    this.log.length = 0;
    this.log.push(...from.log);
    this.round = from.round;
    this.remaining = [...from.remaining];
    this.over = from.over;
    for (let v = 0; v < VENT_COUNT; v++) this.sealedIn[v] = from.sealedIn[v];
  }
}

/**
 * A restorable snapshot of a run.
 *
 * EXISTS BECAUSE `undo()` IS NOT A TRIAL MECHANISM AND MUST NOT BE USED AS ONE. Undo is deliberately
 * bounded to the current round — unwinding a number a rival has already seen on their summary is
 * worse than not offering undo at all — so it REFUSES once a placement has rolled the round over.
 * Code that used place()+undo() to try a candidate therefore worked for three marks and then
 * silently left the fourth on the plate forever, corrupting the run from round two onward while
 * every unit test stayed green. Trials go through here; the player's undo stays bounded.
 */
export interface Snapshot {
  piece: Int8Array;
  rot: Int8Array;
  weeps: WeepSet;
  sealedIn: number[];
  round: number;
  remaining: number[];
  over: boolean;
  logLen: number;
}

export function snapshot(g: Game): Snapshot {
  return {
    piece: Int8Array.from(g.plate.piece),
    rot: Int8Array.from(g.plate.rot),
    weeps: Uint8Array.from(g.weepSet),
    sealedIn: [...g.sealedIn],
    round: g.round,
    remaining: [...g.remaining],
    over: g.over,
    logLen: g.log.length,
  };
}

export function restore(g: Game, s: Snapshot): void {
  g.plate.piece.set(s.piece);
  g.plate.rot.set(s.rot);
  g.weepSet.set(s.weeps);
  for (let i = 0; i < VENT_COUNT; i++) g.sealedIn[i] = s.sealedIn[i];
  g.round = s.round;
  g.remaining = [...s.remaining];
  g.over = s.over;
  g.log.length = s.logLen;
}

export interface ScoreInput {
  sealedIn: readonly number[];
  weeps?: WeepSet;
  scraps?: number;
  claimTier?: (vent: number) => 0 | 1;
}

/**
 * Score any plate — yours, a rival's decoded off the wire, or a bot's.
 *
 * A free function rather than a Game method precisely so a peer can score a plate it did not play,
 * which is how the summary shows everybody's term-by-term breakdown from one 49-byte packet instead
 * of a structure that would have to stay in step with this file forever.
 */
export function scorePlate(plate: Plate, mode: Mode, input: ScoreInput): ScoreDetail {
  const weeps = input.weeps ?? noWeeps();
  const g = buildGraph(plate, weeps);

  const rc = longestRun(g, CULVERT);
  const rf = longestRun(g, FILAMENT);

  let claim = 0;
  if (mode.ventsScore) {
    for (let v = 0; v < VENT_COUNT; v++) {
      if (input.sealedIn[v] < 0) continue;
      const tier = input.claimTier ? input.claimTier(v) : 0;
      claim += mode.claim[tier];
    }
  }

  const largest = mode.ventsScore ? largestVentComponent(g) : 0;
  // Over EVERY vent-holding component, not just the biggest — see ventComponentSizes for why a
  // largest-only term turned out to be invisible to both a search and a plan.
  let net = 0;
  if (mode.ventsScore) {
    for (const k of ventComponentSizes(g)) net += mode.net[Math.min(k, mode.net.length - 1)] ?? 0;
  }
  const run = (rc.length + rf.length) * mode.runPer;

  let off = 0;
  if (mode.offPath !== 0) {
    off = (offPathCells(plate, rc.path, CULVERT) + offPathCells(plate, rf.path, FILAMENT)) * mode.offPath;
  }

  const ends = openEnds(plate) * mode.ends;

  // Each weeping boundary is marked exactly ONCE — from the side of whichever fitting was drawn
  // second, since weepsOf only ever looks at neighbours that already exist. buildGraph then blocks
  // conduction if EITHER side carries the mark, so one mark is both the charge and the cut.
  let weepN = 0;
  for (let i = 0; i < weeps.length; i++) if (weeps[i]) weepN++;
  const weep = weepN * mode.weep;

  const scrap = (input.scraps ?? 0) * mode.scrap;
  const total = claim + net + run + off + ends + weep + scrap;

  return {
    claim,
    net,
    run,
    ends,
    offPath: off,
    weep,
    scrap,
    total,
    sealedIn: [...input.sealedIn],
    largest,
    runC: rc.length,
    runF: rf.length,
    pathC: rc.path,
    pathF: rf.path,
    bounded: rc.bounded || rf.bounded,
    maxExpansions: Math.max(rc.expansions, rf.expansions),
  };
}

export { clonePlate };
